import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getAgentBySlug, ORDER_TRACKING_AGENT } from "./data/agents";
import { getPersonaById, shopifyCustomerToPersona, SHOPIFY_PERSONA_ID_PREFIX } from "./data/personas";
import { resolveVerticalPersona } from "./data/verticals";
import { useSession } from "./context/SessionContext";
import { PersonaConfig, QueryType } from "./types";
import type { ShopifyCustomerSummary } from "./shopify/client";
import { QuerySelectionStep } from "./steps/QuerySelectionStep";
import { CallSurfaceStep } from "./steps/CallSurfaceStep";
import { CopaCallSurfaceStep } from "./steps/CopaCallSurfaceStep";

const VALID_QUERIES: QueryType[] = ["wismo", "cancellation", "store-desk", "product-rec", "refund-status", "baggage-reimbursement", "returned-payment", "compliance-hold", "missing-invoice", "integration-sync"];

function useResolvePersona(personaParam: string | null): PersonaConfig | undefined {
  const queryClient = useQueryClient();
  if (!personaParam) return undefined;

  const local = getPersonaById(personaParam);
  if (local) return local;

  // Seeded demo verticals (Electronics `elec_…`, Tractor Supply `tsc_…`).
  const vertical = resolveVerticalPersona(personaParam);
  if (vertical) return vertical;

  if (personaParam.startsWith(SHOPIFY_PERSONA_ID_PREFIX)) {
    const customers = queryClient.getQueryData<ShopifyCustomerSummary[]>(["shopify_customers"]);
    const rawId     = personaParam.slice(SHOPIFY_PERSONA_ID_PREFIX.length);
    const customer  = customers?.find((c) => String(c.id) === rawId);
    if (customer) return shopifyCustomerToPersona(customer);
  }

  return undefined;
}

export function StepRouter() {
  const [searchParams] = useSearchParams();
  const { state } = useSession();

  const agentSlug    = searchParams.get("agent");
  const personaParam = searchParams.get("persona");
  const queryParam   = searchParams.get("query") as QueryType | null;

  const agent     = agentSlug ? getAgentBySlug(agentSlug) : undefined;
  const persona   = useResolvePersona(personaParam);
  const queryType: QueryType | null = queryParam && VALID_QUERIES.includes(queryParam) ? queryParam : null;

  // Step 1 — pick a customer (agent drives which brand's CRM is shown).
  if (!persona) return <QuerySelectionStep persona={undefined} queryType={null} agent={agent} />;

  const resolvedAgent = agent ?? ORDER_TRACKING_AGENT;
  const effectiveQuery = (queryType ?? state.queryType ?? "wismo") as QueryType;

  // Copa keeps its bespoke dashboard (its landing supplies the scenario).
  if (resolvedAgent.slug === "copa-airlines")
    return <CopaCallSurfaceStep agent={resolvedAgent} queryType={effectiveQuery} persona={persona} />;

  // Everything else → unified call experience (scenario + Start handled in-screen).
  return <CallSurfaceStep agent={resolvedAgent} queryType={effectiveQuery} persona={persona} />;
}
